/**
 * MDXEditor plugin that keeps each quoted paragraph in its own Lexical
 * QuoteNode.
 *
 * MDXEditor imports a markdown blockquote as one QuoteNode wrapping one
 * ParagraphNode per paragraph. Lexical's block commands (`$setBlocksType`,
 * used by the block type select) only replace the innermost block, so
 * choosing "Paragraph" swaps the inner paragraphs and leaves the quote in
 * place. Lexical's own QuoteNode holds inline content directly, which is the
 * shape those commands expect.
 */
import { $createQuoteNode, $isQuoteNode, QuoteNode } from '@lexical/rich-text';
import {
  addExportVisitor$,
  addImportVisitor$,
  type LexicalExportVisitor,
  type MdastImportVisitor,
  realmPlugin
} from '@mdxeditor/editor';
import type { ElementNode } from 'lexical';
import type * as Mdast from 'mdast';

/** Runs ahead of MDXEditor's built-in blockquote visitors (priority 0). */
const QUOTE_VISITOR_PRIORITY = 100;

const isParagraphOnlyBlockquote = (
  node: Mdast.Nodes
): node is Mdast.Blockquote =>
  node.type === 'blockquote' &&
  node.children.every((child) => child.type === 'paragraph');

/**
 * Imports `> a\n>\n> b` as two sibling QuoteNodes holding inline content.
 * Blockquotes containing other blocks (lists, headings, nested quotes) keep
 * MDXEditor's default import.
 */
const quoteParagraphsImportVisitor: MdastImportVisitor<Mdast.Blockquote> = {
  testNode: isParagraphOnlyBlockquote,
  visitNode({ mdastNode, lexicalParent, actions }) {
    mdastNode.children.forEach((paragraph) => {
      const quote = $createQuoteNode();

      (lexicalParent as ElementNode).append(quote);
      actions.visitChildren(paragraph as Mdast.Paragraph, quote);
    });
  },
  priority: QUOTE_VISITOR_PRIORITY
};

/**
 * Exports each QuoteNode as a blockquote holding one paragraph, and merges
 * consecutive blockquotes back into one so the markdown round-trips.
 */
const quoteParagraphsExportVisitor: LexicalExportVisitor<
  QuoteNode,
  Mdast.Blockquote
> = {
  testLexicalNode: $isQuoteNode,
  visitLexicalNode({ lexicalNode, mdastParent, actions }) {
    const paragraph: Mdast.Paragraph = { type: 'paragraph', children: [] };

    actions.visitChildren(lexicalNode, paragraph);
    actions.appendToParent(mdastParent, {
      type: 'blockquote',
      children: [paragraph]
    });
  },
  // MDXEditor's default visitors append a node before filling its children,
  // and a join would detach that node. Only nodes that arrive already filled
  // (the ones built above) are safe to merge.
  shouldJoin: (prevNode, currentNode) =>
    prevNode.type === 'blockquote' &&
    currentNode.type === 'blockquote' &&
    currentNode.children.length > 0,
  join<T extends Mdast.RootContent>(prevNode: T, currentNode: T): T {
    const prev = prevNode as Mdast.Blockquote;
    const current = currentNode as Mdast.Blockquote;

    return {
      ...prev,
      children: [...prev.children, ...current.children]
    } as T;
  },
  priority: QUOTE_VISITOR_PRIORITY
};

/** Register after `quotePlugin()`, which provides the QuoteNode class. */
export const quoteParagraphsPlugin = realmPlugin({
  init(realm) {
    realm.pubIn({
      [addImportVisitor$]: quoteParagraphsImportVisitor,
      [addExportVisitor$]: quoteParagraphsExportVisitor
    });
  }
});
